
import {serializeUrl} from "@Utils/url.util";

export class AdvancedFilter {
    static advancedFiltersKey = 'advancedFilters';
    static advancedFiltersArrayKey = '';
    static allFilterOption = 'all';

    static filterTypeActiveClass = '-filter-type-active';
    static filterTypeLoadingClass = '-filter-type-loading';
    static filterTypeDisabledInactiveClasses = '-filter-type-disabled';
    static filterTypeDisabledActiveClasses = `-filter-type-disabled-active`;
    static filterTypeButtonContainerClasses = `${AdvancedFilter.filterTypeActiveClass} ${AdvancedFilter.filterTypeLoadingClass} ${AdvancedFilter.filterTypeDisabledInactiveClasses} ${AdvancedFilter.filterTypeDisabledActiveClasses}`;

    static filterOptionActiveClass = '-filter-option-active';
    static filterOptionClasses = `${AdvancedFilter.filterOptionActiveClass}`;

    static LOADING_STATE = 'loading';
    static ACTIVE_STATE = 'active';
    static INACTIVE_STATE = 'inactive';
    static DISABLED_STATE = 'disabled';
    static DISABLED_ACTIVE_STATE = 'disabled-active';

    /**
     * We do not show filter type options when the filter is loading / disabled
     *
     * @param {jQuery} filterTypeButton
     */
    static shouldShowFilterTypeOptions(filterTypeButton) {
        const classes = [AdvancedFilter.filterTypeLoadingClass, AdvancedFilter.filterTypeDisabledInactiveClasses, AdvancedFilter.filterTypeDisabledInactiveClasses];

        for (let i = 0; i < classes.length; i++) {
            if (filterTypeButton.hasClass(classes[i])) {
                return false;
            }
        }
        return true;
    }

    /**
     * @param {string} filterOption
     * @param {string} advancedFiltersArrayKey
     */
    constructor(filterOption, advancedFiltersArrayKey) {
        this.advancedFiltersArrayKey = advancedFiltersArrayKey;
        this.filterOption = this.constructor.getValidFilterOption(filterOption, this.advancedFiltersArrayKey);
        this.oldFilterOption = this.filterOption;
        this.setWasOptionSelectedByUser(true);
        this.setLastOptionSelectedByUser(this.filterOption);
        this.setActiveState();
    }

    /**
     * @param {boolean} wasOptionSelectedByUser
     */
    setWasOptionSelectedByUser(wasOptionSelectedByUser) {
        this.wasOptionSelectedByUser = wasOptionSelectedByUser;
    }

    /**
     * @param {string} lastOptionSelectedByUser
     */
    setLastOptionSelectedByUser(lastOptionSelectedByUser) {
        this.lastOptionSelectedByUser = lastOptionSelectedByUser;
    }

    setActiveState() {
        this.state = this.isFilterActive() ? AdvancedFilter.ACTIVE_STATE : AdvancedFilter.INACTIVE_STATE;
    }

    setLoadingState() {
        this.state = AdvancedFilter.LOADING_STATE;
    }

    /**
     * @return {boolean}
     */
    isLoadingState() {
        return this.state === AdvancedFilter.LOADING_STATE;
    }

    setDisabledState() {
        this.state = AdvancedFilter.DISABLED_STATE;
    }

    /**
     * @return {boolean}
     */
    isDisabledState() {
        return this.state === AdvancedFilter.DISABLED_STATE;
    }

    /**
     * @param {string} state
     */
    setState(state) {
        this.state = state;
    }

    /**
     * @return {string}
     */
    getState() {
        return this.state;
    }

    static getStateClassMapForFilterTypeContainer() {
        const map = {};
        map[this.LOADING_STATE] = this.filterTypeLoadingClass;
        map[this.ACTIVE_STATE] = this.filterTypeActiveClass;
        map[this.INACTIVE_STATE] = '';
        map[this.DISABLED_ACTIVE_STATE] = this.filterTypeDisabledActiveClasses;
        map[this.DISABLED_STATE] = this.filterTypeDisabledInactiveClasses;
        return map;
    }

    static getStateClassMapForFilterOptions() {
        const map = {};
        map[this.ACTIVE_STATE] = this.filterOptionActiveClass;
        map[this.INACTIVE_STATE] = '';
        return map;
    }

    /**
     * @param {string} filterOption
     * @param {string} advancedFiltersArrayKey
     * @return {string}
     */
    static getValidFilterOption(filterOption, advancedFiltersArrayKey) {
        const filterOptionEl = $(`.js-filter-type-option[data-filter-type=${this.advancedFiltersArrayKey}][data-filter-option=${filterOption}]`);

        if (filterOptionEl.length > 0) {
            return filterOption;
        }
        return AdvancedFilter.allFilterOption;
    }

    /**
     * @return {AdvancedFilter}
     */
    static fromPageHtml() {
        if (this.advancedFiltersArrayKey === '') {
            throw Error('static prop `advancedFiltersArrayKey` not added to the class');
        }
        const selectedFilterOption = $(`.${this.getFilterOptionsDropdownClassname()} .js-filter-type-option${'.' + AdvancedFilter.filterOptionActiveClass}`),
            filterOption = $(selectedFilterOption).attr('data-filter-option');

        return new this(filterOption, this.advancedFiltersArrayKey);
    }

    /**
     * @return {string}
     */
    static getFilterOptionsDropdownClassname() {
        return `js-${this.advancedFiltersArrayKey}-options`;
    }

    /**
     * @param {jQuery} chosenFilterOption
     * @return {boolean}
     */
    static isChosenFilterOptionActive(chosenFilterOption) {
        return chosenFilterOption.hasClass(AdvancedFilter.filterOptionActiveClass);
    }

    /**
     * @return {{filterOption: string, wasOptionSelectedByUser: boolean, lastOptionSelectedByUser: string}}
     */
    getPopStateInfo() {
        return {
            filterOption: this.filterOption,
            lastOptionSelectedByUser: this.lastOptionSelectedByUser,
            wasOptionSelectedByUser: this.wasOptionSelectedByUser
        };
    }

    /**
     * @return {boolean}
     */
    wasFilterUpdated() {
        return this.oldFilterOption !== this.filterOption;
    }

    resetOldFilterState() {
        return this.oldFilterOption = this.filterOption;
    }

    /**
     * @param {AdvancedFilter} advancedFilter
     */
    handleUpdatedFilter(advancedFilter) {
        if (this.filterOption === advancedFilter.filterOption) {
            return;
        }
        this.filterOption = advancedFilter.filterOption;
        this.setActiveState();
        this.updateView();
        this.setWasOptionSelectedByUser(advancedFilter.wasOptionSelectedByUser);

        if (advancedFilter.wasOptionSelectedByUser) {
            this.lastOptionSelectedByUser = advancedFilter.filterOption;
        }
    }

    /**
     * The popstate object dictates the last selected option here
     *
     * @param {AdvancedFilter} advancedFilter
     */
    handleUpdatedFilterFromPopstate(advancedFilter) {
        this.handleUpdatedFilter(advancedFilter);
        this.setLastOptionSelectedByUser(advancedFilter.lastOptionSelectedByUser);
    }

    updateView() {
        const chosenFilterOption = this.getChosenFilterOption();
        this.updateFilterOptions(chosenFilterOption);
        this.updateFilterTypeButton(chosenFilterOption);
        PMW.posterGallery.updateActiveFilterIcon();
    }

    /**
     * @return {jQuery}
     */
    getFilterTypeButtonContainer() {
        return $(`.js-filter-type[data-filter-type=${this.advancedFiltersArrayKey}]`);
    }

    /**
     * @return {jQuery}
     */
    getChosenFilterOption() {
        return this.getFilterOptionEl(this.filterOption);
    }

    /**
     * @param {string} filterOption
     * @return {*|jQuery|HTMLElement}
     */
    getFilterOptionEl(filterOption) {
        return $(`.js-filter-type-option[data-filter-type=${this.advancedFiltersArrayKey}][data-filter-option=${filterOption}]`);
    }

    revertToLastUserChosenOption() {
        this.wasOptionSelectedByUser = true;
        this.filterOption = this.lastOptionSelectedByUser;
        this.setActiveState();
        this.updateView();
    }

    /**
     * @param {jQuery} chosenFilterOption
     */
    updateFilterOptions(chosenFilterOption) {
        const allOptionsForFilterType = chosenFilterOption.closest('.js-filter-type-options-container').find('.js-filter-type-option'),
            classMapForFilterOptions = AdvancedFilter.getStateClassMapForFilterOptions(),
            stateForUnselectedOptions = AdvancedFilter.INACTIVE_STATE,
            stateForSelectedOption = AdvancedFilter.ACTIVE_STATE;

        allOptionsForFilterType.removeClass(AdvancedFilter.filterOptionClasses).addClass(classMapForFilterOptions[stateForUnselectedOptions]);
        chosenFilterOption.removeClass(AdvancedFilter.filterOptionClasses).addClass(classMapForFilterOptions[stateForSelectedOption]);
    }

    getFilterTypeContainerState() {
        if (!this.isDisabledState()) {
            return this.state;
        }

        if (this.isFilterActive()) {
            return AdvancedFilter.DISABLED_ACTIVE_STATE;
        }
        return AdvancedFilter.DISABLED_STATE;
    }

    /**
     * @param {jQuery} chosenFilterOption
     * @param {jQuery} filterTypeButtonContainer
     * @return {string}
     */
    getFilterTypeButtonText(chosenFilterOption, filterTypeButtonContainer) {
        if (chosenFilterOption.attr('data-filter-option') === AdvancedFilter.allFilterOption) {
            return filterTypeButtonContainer.attr('data-display-name');
        }
        return chosenFilterOption.attr('data-display-name');
    }

    /**
     * @param {jQuery} chosenFilterOption
     */
    updateFilterTypeButton(chosenFilterOption) {
        const filterTypeButtonContainer = this.getFilterTypeButtonContainer(),
            filterTypeButtonText = this.getFilterTypeButtonText(chosenFilterOption, filterTypeButtonContainer),
            filterTypeContainerState = this.getFilterTypeContainerState(),
            filterTypeContainerClass = AdvancedFilter.getStateClassMapForFilterTypeContainer()[filterTypeContainerState];

        filterTypeButtonContainer.find('.filter-type-text').text(filterTypeButtonText);
        filterTypeButtonContainer.removeClass(AdvancedFilter.filterTypeButtonContainerClasses).addClass(filterTypeContainerClass);
    }

    /**
     * @return {boolean}
     */
    isFilterActive() {
        return this.filterOption !== AdvancedFilter.allFilterOption;
    }

    /**
     * @param {string} url
     * @return {string}
     */
    updateUrl(url) {
        const urlObj = new URL(url);
        return serializeUrl(this.updateAdvancedFilterInUrl(urlObj));
    }

    /**
     * @param {URL} urlObj
     * @return {URL}
     */
    updateAdvancedFilterInUrl(urlObj) {
        if (this.isFilterActive()) {
            urlObj.searchParams.set(this.advancedFiltersArrayKey, this.filterOption);
        } else {
            urlObj.searchParams.delete(this.advancedFiltersArrayKey);
        }
        return urlObj;
    }
}